import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import useStyles from '../../../styles/alertForm';
import { defaultSensorsLimit, getTooltipSlotProps, selectOutlineSx } from '../helpers';
import { Analysisoptions as LocationAnalysisOptions } from '../../../services/api';
import { AnalysisOptions } from './AnalysisOptionsContainer';
import { useAppSelector } from '../../../state/store';
import { getCurrentLocation, getSensorsById } from '../../../state/selectors';

interface SensorsFilterProps {
  locValue: LocationAnalysisOptions | null;
  onChange: (value: Partial<AnalysisOptions>) => void;
  allowEditing: boolean;
}

type SensorListMode = 'whiteListSensors' | 'blackListSensors';

function SensorsFilterSection({ locValue, onChange, allowEditing }: SensorsFilterProps) {
  const theme = useTheme();
  const { classes } = useStyles();
  const currentLocation = useAppSelector(getCurrentLocation);
  const sensorsById = useAppSelector(getSensorsById);

  const locSensorsLength = useMemo(
    () =>
      Array.from(sensorsById.values()).filter(
        (item) => item.location === currentLocation || item.location?.startsWith(currentLocation)
      ).length,
    [sensorsById, currentLocation]
  );

  // Determine mode from the current data rather than only at mount, so the
  // toggle stays correct if `locValue` changes from outside this component.
  const derivedMode: SensorListMode =
    locValue?.blackListSensors && locValue.blackListSensors.length > 0
      ? 'blackListSensors'
      : 'whiteListSensors';

  const [mode, setMode] = useState<SensorListMode>(derivedMode);
  const [inputValue, setInputValue] = useState('');

  // Keep `mode` in sync if the underlying data changes externally (e.g. a
  // "reset filters" action elsewhere), without fighting the user's manual
  // toggle while they're actively editing.
  const lastLocValueRef = useRef(locValue);
  useEffect(() => {
    if (lastLocValueRef.current !== locValue) {
      lastLocValueRef.current = locValue;
      setMode(derivedMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locValue]);

  const activeSensors = useMemo(
    () =>
      (mode === 'whiteListSensors' ? locValue?.whiteListSensors : locValue?.blackListSensors) ?? [],
    [mode, locValue]
  );

  // update location analysis options
  const handleLocationChange = (patch: Partial<LocationAnalysisOptions>) => {
    onChange(patch as Partial<AnalysisOptions>);
  };

  // only one mode of sensors are allowed
  const commitActiveList = (updated: string[]) => {
    const otherKey: SensorListMode =
      mode === 'whiteListSensors' ? 'blackListSensors' : 'whiteListSensors';

    handleLocationChange({ [mode]: updated, [otherKey]: [] });
  };

  const handleModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: SensorListMode | null
  ) => {
    if (!newMode || newMode === mode) return;
    setMode(newMode);
    setInputValue('');
  };

  const addSensors = (raw: string) => {
    const newIds = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    if (newIds.length === 0) return;

    const merged = Array.from(new Set([...activeSensors, ...newIds]));
    commitActiveList(merged);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) {
        addSensors(inputValue);
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && inputValue === '' && activeSensors.length > 0) {
      // Quick-delete last chip on backspace when input is empty
      commitActiveList(activeSensors.slice(0, -1));
    }
  };

  const handleChipDelete = (sensorId: string) => {
    commitActiveList(activeSensors.filter((id) => id !== sensorId));
  };

  const listLabel = mode === 'whiteListSensors' ? 'Whitelist Sensors' : 'Blacklist Sensors';
  const listTooltip =
    mode === 'whiteListSensors'
      ? 'Only sensor ids in this list will be considered'
      : 'Sensor ids in this list will not be considered';

  return (
    <Box sx={{ mt: 2, ml: 1 }} className={classes.formContainer}>
      <Typography variant="body1" sx={{ width: '100%' }}>
        for number of sensors, this threshold applies to is
      </Typography>
      <Grid container spacing={2} sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
        <Grid size={{ xs: 6, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Sensors Limit"
            type="number"
            value={locValue?.sensorsLimit ?? defaultSensorsLimit}
            onChange={(e) =>
              handleLocationChange({
                sensorsLimit: e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
            placeholder="e.g. 10"
            sx={selectOutlineSx(theme)}
            slotProps={getTooltipSlotProps('Number of sensors that must match before alerting')}
            disabled={!allowEditing}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <span>
            sensors{' '}
            {locSensorsLength > 0 && (
              <>
                out of <b>{locSensorsLength} sensors</b>
              </>
            )}
          </span>
        </Grid>

        <Grid size={{ xs: 12, sm: 5 }} sx={{ minWidth: 0 }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            size="small"
            onChange={handleModeChange}
            fullWidth
            sx={{ mb: 1 }}
            disabled={!allowEditing}
            aria-label="Sensor filter mode"
          >
            <ToggleButton
              value="whiteListSensors"
              sx={{ minWidth: 0, px: 1 }}
              aria-label="Whitelist"
            >
              Whitelist
            </ToggleButton>
            <ToggleButton
              value="blackListSensors"
              sx={{ minWidth: 0, px: 1 }}
              aria-label="Blacklist"
            >
              Blacklist
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
        <Grid size={{ xs: 12, sm: 7 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {listLabel} (optional)
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: allowEditing ? 1 : 0 }}
          >
            {activeSensors.map((sensorId) => (
              <Chip
                key={sensorId}
                label={sensorId}
                size="small"
                onDelete={allowEditing ? () => handleChipDelete(sensorId) : undefined}
              />
            ))}
          </Stack>

          {allowEditing && (
            <TextField
              fullWidth
              size="small"
              label={`Add ${listLabel.toLowerCase()}`}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onBlur={() => {
                if (inputValue.trim()) {
                  addSensors(inputValue);
                  setInputValue('');
                }
              }}
              placeholder="Type a sensor id and press Enter or comma"
              sx={selectOutlineSx(theme)}
              slotProps={getTooltipSlotProps(listTooltip)}
              disabled={!allowEditing}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default SensorsFilterSection;
